import { c as _c } from "react/compiler-runtime";
import { CountryCodes, searchFiltersRouteParam } from '@spa/lib/const';
import { ListAttribution, WishlistsActionsOriginType } from '@spa/lib/tracking/trackWishlistsActions';
import { ProductAsset, ProductAssetWithRequiredDimensions } from '@spa/components/ProductAssetTypes';
import ProductPrice, { BasePrice, Capacity, ProductPriceType } from '@spa/components/product-price/product-price.component';
import { ReactElement, useMemo } from 'react';
import { DynamicProductTestRatingComponent } from '@spa/components/product-test-rating/dynamic-product-test-rating.component';
import Image from 'next/image';
import ImageComponent from '@spa/components/image-component/image.component';
import ImageSectionComponent from '../../image-section/image-section.component';
import Link from 'next/link';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { ProductActionsOriginType } from '@spa/lib/tracking/trackProductActions';
import { ProductBadge } from '@spa/components/product-info/ProductInfoProductTypes';
import ProductBadgesComponent from '@spa/components/product-info/product-badges/product-badges.component';
import ProductEnergyLabelComponent from '@spa/components/product-energy-label/product-energy-label.component';
import { ProductPreOrderSettings } from '@spa/generated/graphql/graphql';
import ProductRatingsComponent from '../../product-ratings/product-ratings.component';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import { StateCartPrice } from '@spa/lib/store/carts/CartsState';
import WishlistComponent from '@spa/components/wishlist/wishlist.component';
import classNames from 'clsx';
import { computeIsProductOnSale } from '@spa/components/product-price/computeIsProductOnSale';
import { convertProductToCommonECommerceEventItem } from '@spa/lib/tracking/convertProductToCommonECommerceEventItem';
import getAuthSessionUserId from '../../../lib/auth/getAuthSessionUserId';
import { getInitialQuantityToBuy } from '@spa/lib/getInitialQuantityToBuy';
import { getProductImageOrFallback } from '@spa/lib/getProductImageOrFallback';
import { isShopStoreType } from '@spa/lib/configs/storeType';
import { scheduleTask } from '@spa/lib/utils/scheduleTask';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import { setListContext } from '@spa/lib/tracking/listContextStorage';
import styles from './product-tile.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import { truncateAtWord } from '@spa/lib/utils/truncateAtWord';
import { useProductTileContext } from './product-tile.context';
import { useSelector } from 'react-redux';
export interface Image {
  readonly url: string;
  readonly width: number;
  readonly height: number;
}
export interface Asset {
  readonly type: string;
  readonly url: string;
  readonly width: number;
  readonly height: number;
}
export interface Brand {
  readonly name: string;
}
export interface Category {
  readonly name: string;
}
export enum StockIndication {
  IN_STOCK = 'IN_STOCK',
  LOW_IN_STOCK = 'LOW_IN_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
}
export interface Product {
  readonly assets: ReadonlyArray<Asset> | null;
  readonly author: string | null;
  readonly artist: string | null;
  readonly energyEfficiencyLabel: ProductAssetWithRequiredDimensions | null;
  readonly energyEfficiencyOverview: ProductAsset | null;
  readonly productDataSheet: ProductAsset | null;
  readonly badges: ReadonlyArray<ProductBadge> | null;
  readonly basePrice: BasePrice | null;
  readonly brand: Brand | null;
  readonly campaignPrice: StateCartPrice | null;
  readonly capacity: Capacity | null;
  readonly categories: ReadonlyArray<Category>;
  readonly categoryWithParents: ReadonlyArray<Category>;
  readonly clickAndCollect: boolean;
  readonly code: string;
  readonly colorVariantImages?: ReadonlyArray<Image | null> | null;
  readonly currentPrice: StateCartPrice;
  readonly homeDelivery: boolean;
  readonly image: Image | null;
  readonly name: string;
  readonly ownBrand: boolean | null;
  readonly path: string;
  readonly preOrderSettings: ProductPreOrderSettings | null;
  readonly recommendedRetailPrice: StateCartPrice | null;
  readonly releaseDate: string | null;
  readonly saleMinimumQuantity?: number | null;
  readonly stockLevelIndication: StockIndication;
  readonly strikePrice: StateCartPrice | null;
  readonly lowestVariantPrice?: StateCartPrice | null;
  readonly isDummy?: boolean;
  readonly averageRating?: number | null;
  readonly reviewCount?: number | null;
}
export const PRODUCT_TILE_SIZES_PLP = '(max-width: 767px) calc(50vw - 42px), (max-width: 1023px) calc(33vw - 85px), calc(25vw - 93px)';
export const PRODUCT_TILE_SIZES_CAROUSEL = '(max-width: 1023px) 240px, 280px';
export interface Props {
  readonly priority: boolean;
  readonly isFlyout?: boolean;
  readonly productActionOrigin: ProductActionsOriginType;
  readonly wishlistActionOrigin: WishlistsActionsOriginType;
  readonly wishlistListAttribution?: ListAttribution;
  readonly productIndex: number;
  readonly product: Product;
  readonly searchFilters?: ReadonlyArray<string>;
  readonly showAddToCartButton?: boolean;
  readonly shouldRenderImage?: boolean;
  readonly sizes?: string;
}
interface ProductTileRuntimeValues {
  readonly countryCode: string;
  readonly formatMessage: NamespacedIntl['formatMessage'];
  readonly fullPath: string;
  readonly isWishlistEnabled: boolean;
  readonly locale: string;
  readonly popularityEventsEnabled: boolean;
  readonly preOrderEnabled: boolean;
  readonly pricesIncludeTax: boolean;
  readonly sendClickEvent: (eventData: {
    readonly doc_id: string;
    readonly user_id: string;
  }) => Promise<unknown>;
  readonly sessionsData: SessionsData;
}
function ProductTileContent(t0) {
  const $ = _c(149);
  const {
    priority,
    product,
    productActionOrigin,
    wishlistActionOrigin,
    wishlistListAttribution,
    productIndex,
    searchFilters,
    isFlyout: t1,
    shouldRenderImage: t2,
    sizes: t3,
    runtimeValues
  } = t0;
  const isFlyout = t1 === undefined ? false : t1;
  const shouldRenderImage = t2 === undefined ? true : t2;
  const sizes = t3 === undefined ? PRODUCT_TILE_SIZES_PLP : t3;
  const {
    showProductTestEnabled
  } = useSelector(selectGeneralSettings);
  const {
    countryCode,
    formatMessage,
    fullPath,
    isWishlistEnabled,
    locale,
    popularityEventsEnabled,
    preOrderEnabled,
    pricesIncludeTax,
    sendClickEvent,
    sessionsData
  } = runtimeValues;
  const {
    authSession,
    session
  } = sessionsData;
  let t4;
  if ($[0] !== priority || $[1] !== sizes) {
    t4 = {
      sizes,
      priority
    };
    $[0] = priority;
    $[1] = sizes;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const imageSettings = t4;
  const {
    assets,
    badges,
    basePrice,
    campaignPrice,
    capacity,
    clickAndCollect,
    code,
    colorVariantImages,
    currentPrice,
    homeDelivery,
    image,
    name,
    path: productPath,
    preOrderSettings,
    recommendedRetailPrice,
    stockLevelIndication,
    strikePrice,
    lowestVariantPrice,
    energyEfficiencyLabel,
    energyEfficiencyOverview,
    productDataSheet,
    isDummy: t5,
    releaseDate,
    saleMinimumQuantity
  } = product;
  const isDummy = t5 === undefined ? false : t5;
  const t6 = campaignPrice ?? null;
  const t7 = strikePrice ?? null;
  const t8 = lowestVariantPrice ?? null;
  let t9;
  if ($[3] !== countryCode || $[4] !== currentPrice || $[5] !== pricesIncludeTax || $[6] !== recommendedRetailPrice || $[7] !== t6 || $[8] !== t7 || $[9] !== t8) {
    t9 = computeIsProductOnSale(countryCode, pricesIncludeTax, {
      currentPrice,
      recommendedRetailPrice,
      campaignPrice: t6,
      strikePrice: t7,
      lowestVariantPrice: t8
    });
    $[3] = countryCode;
    $[4] = currentPrice;
    $[5] = pricesIncludeTax;
    $[6] = recommendedRetailPrice;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const isProductOnSale = t9;
  let t10;
  if ($[11] !== assets || $[12] !== image) {
    t10 = getProductImageOrFallback(image, assets);
    $[11] = assets;
    $[12] = image;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const imageOrFallBack = t10;
  let pdpUrl;
  if ($[14] !== productPath || $[15] !== searchFilters) {
    pdpUrl = new URL(productPath, "http://localhost");
    if (searchFilters?.length) {
      pdpUrl.searchParams.set(searchFiltersRouteParam, JSON.stringify(searchFilters));
    }
    $[14] = productPath;
    $[15] = searchFilters;
    $[16] = pdpUrl;
  } else {
    pdpUrl = $[16];
  }
  let t11;
  if ($[17] !== assets) {
    t11 = assets?.find(_temp);
    $[17] = assets;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const testRatingAsset = t11;
  let t12;
  if ($[19] !== badges) {
    t12 = isShopStoreType ? badges : badges?.filter(_temp2) ?? null;
    $[19] = badges;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const tileBadges = t12;
  const productIsUnavailable = stockLevelIndication === StockIndication.OUT_OF_STOCK;
  const t13 = !isFlyout;
  let t14;
  if ($[21] !== isFlyout || $[22] !== productIsUnavailable || $[23] !== t13) {
    t14 = classNames(styles["product-tile"], {
      [styles["product-tile--unavailable"]]: productIsUnavailable,
      [styles["product-tile--hidden-layer"]]: t13,
      [styles["product-tile--flyout"]]: isFlyout
    });
    $[21] = isFlyout;
    $[22] = productIsUnavailable;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const productTileClass = t14;
  const t15 = colorVariantImages && colorVariantImages.length > 0;
  let t16;
  if ($[25] !== t15) {
    t16 = classNames({
      [styles["product-tile__image-section"]]: true,
      [styles["product-tile__image-section--with-variants"]]: t15
    });
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const imageSectionClass = t16;
  let t17;
  if ($[27] !== formatMessage || $[28] !== name) {
    t17 = authorOrArtist => {
      let linkLabel = name;
      if (authorOrArtist && !name.startsWith(authorOrArtist)) {
        linkLabel = `${authorOrArtist} - ${name}`;
      }
      linkLabel = truncateAtWord(`${formatMessage("linkLabelPrefix")} ${linkLabel}`, 80);
      return linkLabel;
    };
    $[27] = formatMessage;
    $[28] = name;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const generateProductTileLinkLabel = t17;
  const isNotCh = countryCode !== "CH";
  const shouldHidePrices = countryCode === CountryCodes.HU;
  const productLinkHref = pdpUrl.pathname + pdpUrl.search;
  let authorOrArtist_0;
  let t18;
  if ($[30] !== generateProductTileLinkLabel || $[31] !== product.artist || $[32] !== product.author) {
    authorOrArtist_0 = product.author?.trim() || product.artist?.trim() || null;
    t18 = generateProductTileLinkLabel(authorOrArtist_0);
    $[30] = generateProductTileLinkLabel;
    $[31] = product.artist;
    $[32] = product.author;
    $[33] = authorOrArtist_0;
    $[34] = t18;
  } else {
    authorOrArtist_0 = $[33];
    t18 = $[34];
  }
  const authorOrArtistAriaLabel = t18;
  const isPreOrderActive = (preOrderSettings?.isActive && preOrderEnabled) ?? false;
  const isPreOrderProduct = (!!preOrderSettings?.validTo && preOrderEnabled) ?? false;
  let t19;
  if ($[35] !== releaseDate) {
    t19 = !releaseDate || new Date(releaseDate) < new Date();
    $[35] = releaseDate;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const isReleased = t19;
  const shouldRenderTestRating = showProductTestEnabled && Boolean(testRatingAsset) && !productIsUnavailable;
  let t20;
  if ($[37] !== authSession || $[38] !== countryCode || $[39] !== popularityEventsEnabled || $[40] !== preOrderEnabled || $[41] !== pricesIncludeTax || $[42] !== productActionOrigin || $[43] !== sendClickEvent || $[44] !== session.guid) {
    t20 = (product_0, productIndex_0) => {
      setListContext(product_0.code, productActionOrigin);
      const anonymousUserId = session.guid;
      const userId = getAuthSessionUserId(authSession);
      const commonECommerceEventItem = convertProductToCommonECommerceEventItem({
        product: product_0,
        listContext: productActionOrigin,
        country: countryCode,
        preOrderEnabled,
        pricesIncludeTax
      });
      scheduleTask(() => {
        tracker.selectItem({
          anonymousId: anonymousUserId,
          authenticatedId: userId,
          ecommerce: {
            items: [{
              ...commonECommerceEventItem,
              item_list_id: productActionOrigin.originId,
              item_list_name: productActionOrigin.originType,
              index: productIndex_0
            }]
          }
        });
        if (popularityEventsEnabled) {
          sendClickEvent({
            doc_id: product_0.code,
            user_id: userId ?? anonymousUserId
          });
        }
      });
    };
    $[37] = authSession;
    $[38] = countryCode;
    $[39] = popularityEventsEnabled;
    $[40] = preOrderEnabled;
    $[41] = pricesIncludeTax;
    $[42] = productActionOrigin;
    $[43] = sendClickEvent;
    $[44] = session.guid;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const handleProductClick = t20;
  let t21;
  if ($[46] !== formatMessage || $[47] !== isPreOrderActive) {
    t21 = (isAvailable, isExclusive, labelAvailable, labelExclusive) => <div className={styles["product-tile__delivery-info"]}>{isAvailable && <><ImageComponent src="/assets/icons/check-circle-filled.svg" height={20} width={20} alt="" /><span className="text-s">{formatMessage(isPreOrderActive ? "availablePreOrder" : isExclusive ? labelExclusive : labelAvailable)}</span></>}</div>;
    $[46] = formatMessage;
    $[47] = isPreOrderActive;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const getDeliveryInfo = t21;
  let t22;
  if ($[49] !== isDummy) {
    t22 = isDummy && <div className={styles["product-tile__dummy"]}>BEISPIEL</div>;
    $[49] = isDummy;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== fullPath || $[52] !== isPreOrderActive || $[53] !== isWishlistEnabled || $[54] !== product || $[55] !== productIndex || $[56] !== saleMinimumQuantity || $[57] !== wishlistActionOrigin || $[58] !== wishlistListAttribution) {
    t23 = isWishlistEnabled && <div className={styles["product-tile__wishlist-wrapper"]}><WishlistComponent afterAuthCallbackUrl={fullPath} product={{
        ...product,
        index: productIndex
      }} quantity={getInitialQuantityToBuy({
        isPreOrderActive,
        saleMinimumQuantity
      })} actionOrigin={wishlistActionOrigin} listAttribution={wishlistListAttribution} /></div>;
    $[51] = fullPath;
    $[52] = isPreOrderActive;
    $[53] = isWishlistEnabled;
    $[54] = product;
    $[55] = productIndex;
    $[56] = saleMinimumQuantity;
    $[57] = wishlistActionOrigin;
    $[58] = wishlistListAttribution;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== isPreOrderActive || $[61] !== isPreOrderProduct || $[62] !== isProductOnSale || $[63] !== productIsUnavailable || $[64] !== shouldRenderImage || $[65] !== shouldRenderTestRating || $[66] !== testRatingAsset || $[67] !== tileBadges) {
    t24 = shouldRenderImage && <div className={styles["product-tile__badges"]}><ProductBadgesComponent badges={tileBadges} isProductOnSale={isProductOnSale} maxVisibleBadges={shouldRenderTestRating ? 1 : undefined} productIsUnavailable={productIsUnavailable} isOnProductTile={true} isPreOrderActive={isPreOrderActive} isPreOrderProduct={isPreOrderProduct} />{shouldRenderTestRating && testRatingAsset && <div className={styles["product-tile__test-rating-wrapper"]}><DynamicProductTestRatingComponent data-testid="product-test-rating" className={styles["product-tile__test-rating"]} variant="product-tile" source={testRatingAsset.url} alt="" /></div>}</div>;
    $[60] = isPreOrderActive;
    $[61] = isPreOrderProduct;
    $[62] = isProductOnSale;
    $[63] = productIsUnavailable;
    $[64] = shouldRenderImage;
    $[65] = shouldRenderTestRating;
    $[66] = testRatingAsset;
    $[67] = tileBadges;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== handleProductClick || $[70] !== product || $[71] !== productIndex) {
    t25 = () => {
      handleProductClick(product, productIndex);
    };
    $[69] = handleProductClick;
    $[70] = product;
    $[71] = productIndex;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== colorVariantImages || $[74] !== imageOrFallBack || $[75] !== imageSettings || $[76] !== name || $[77] !== shouldRenderImage) {
    t26 = shouldRenderImage && <><div className={styles["product-tile__image"]} aria-hidden="true"><ImageSectionComponent url={imageOrFallBack.url} alt={name} {...imageSettings} /></div>{colorVariantImages && colorVariantImages.length > 0 && <div data-testid="product-test-color-variation" className={classNames(styles["product-tile__variants-selection"])}>{colorVariantImages.map(_temp3)}</div>}</>;
    $[73] = colorVariantImages;
    $[74] = imageOrFallBack;
    $[75] = imageSettings;
    $[76] = name;
    $[77] = shouldRenderImage;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== authorOrArtistAriaLabel || $[80] !== code || $[81] !== imageSectionClass || $[82] !== productLinkHref || $[83] !== t25 || $[84] !== t26) {
    t27 = <Link className={imageSectionClass} prefetch={false} href={productLinkHref} onClick={t25} aria-label={authorOrArtistAriaLabel} tabIndex={0} data-track-id="product" data-product-id={code}>{t26}</Link>;
    $[79] = authorOrArtistAriaLabel;
    $[80] = code;
    $[81] = imageSectionClass;
    $[82] = productLinkHref;
    $[83] = t25;
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== isFlyout) {
    t28 = classNames(styles["product-tile__content"], {
      [styles["product-tile__content--flyout"]]: isFlyout
    });
    $[86] = isFlyout;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== handleProductClick || $[89] !== product || $[90] !== productIndex) {
    t29 = () => {
      handleProductClick(product, productIndex);
    };
    $[88] = handleProductClick;
    $[89] = product;
    $[90] = productIndex;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== authorOrArtist_0) {
    t30 = authorOrArtist_0 && <span className={classNames(styles["product-tile__author"], "text-s")}>{authorOrArtist_0}</span>;
    $[92] = authorOrArtist_0;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  const t31 = Boolean(authorOrArtist_0);
  let t32;
  if ($[94] !== isFlyout || $[95] !== t31) {
    t32 = classNames(styles["product-tile__product-name"], "body-bold", {
      [styles["product-tile__product-name--line-clamp-2"]]: t31,
      [styles["product-tile__product-name--flyout"]]: isFlyout
    });
    $[94] = isFlyout;
    $[95] = t31;
    $[96] = t32;
  } else {
    t32 = $[96];
  }
  let t33;
  if ($[97] !== name || $[98] !== t32) {
    t33 = <span className={t32}>{name}</span>;
    $[97] = name;
    $[98] = t32;
    $[99] = t33;
  } else {
    t33 = $[99];
  }
  let t34;
  if ($[100] !== t30 || $[101] !== t33) {
    t34 = <span className={styles["product-tile__text-group"]}>{t30}{t33}</span>;
    $[100] = t30;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== productLinkHref || $[104] !== t29 || $[105] !== t34) {
    t35 = <Link prefetch={false} href={productLinkHref} onClick={t29} tabIndex={0}>{t34}</Link>;
    $[103] = productLinkHref;
    $[104] = t29;
    $[105] = t34;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  let t36;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = classNames(styles["product-tile__product-price"]);
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  let t37;
  if ($[108] !== basePrice || $[109] !== campaignPrice || $[110] !== capacity || $[111] !== countryCode || $[112] !== currentPrice || $[113] !== locale || $[114] !== lowestVariantPrice || $[115] !== pricesIncludeTax || $[116] !== recommendedRetailPrice || $[117] !== shouldHidePrices || $[118] !== strikePrice) {
    t37 = !shouldHidePrices && <ProductPrice basePrice={basePrice ?? null} capacity={capacity ?? null} currentPrice={currentPrice} recommendedRetailPrice={recommendedRetailPrice} campaignPrice={campaignPrice ?? null} strikePrice={strikePrice ?? null} lowestVariantPrice={lowestVariantPrice ?? null} pricesIncludeTax={pricesIncludeTax} locale={locale} country={countryCode} type={ProductPriceType.TILE} reserveStrikePriceSpace={true} reserveBasePriceSpace={true} dataTestIdContext="plp" />;
    $[108] = basePrice;
    $[109] = campaignPrice;
    $[110] = capacity;
    $[111] = countryCode;
    $[112] = currentPrice;
    $[113] = locale;
    $[114] = lowestVariantPrice;
    $[115] = pricesIncludeTax;
    $[116] = recommendedRetailPrice;
    $[117] = shouldHidePrices;
    $[118] = strikePrice;
    $[119] = t37;
  } else {
    t37 = $[119];
  }
  let t38;
  if ($[120] !== energyEfficiencyLabel || $[121] !== energyEfficiencyOverview || $[122] !== productDataSheet) {
    t38 = energyEfficiencyLabel && <ProductEnergyLabelComponent efficiencyClass={energyEfficiencyLabel} overview={energyEfficiencyOverview} datasheet={productDataSheet} />;
    $[120] = energyEfficiencyLabel;
    $[121] = energyEfficiencyOverview;
    $[122] = productDataSheet;
    $[123] = t38;
  } else {
    t38 = $[123];
  }
  let t39;
  if ($[124] !== t37 || $[125] !== t38) {
    t39 = <div className={t36}>{t37}{t38}</div>;
    $[124] = t37;
    $[125] = t38;
    $[126] = t39;
  } else {
    t39 = $[126];
  }
  let t40;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <ProductRatingsComponent averageRating={null} reviewCount={null} />;
    $[127] = t40;
  } else {
    t40 = $[127];
  }
  let t41;
  if ($[128] !== clickAndCollect || $[129] !== getDeliveryInfo || $[130] !== homeDelivery || $[131] !== isFlyout || $[132] !== isNotCh || $[133] !== isPreOrderActive || $[134] !== isReleased || $[135] !== productIsUnavailable) {
    t41 = (!productIsUnavailable || isPreOrderActive) && isNotCh && !isFlyout && isShopStoreType && <div className={classNames({
      [styles["product-tile__delivery-infos"]]: true,
      [styles["product-tile__delivery-infos--justify-end"]]: clickAndCollect && !homeDelivery
    })}>{getDeliveryInfo(homeDelivery, !clickAndCollect, "availableOnline", "availableOnlineOnly")}{!isPreOrderActive && isReleased && getDeliveryInfo(clickAndCollect, !homeDelivery, "deliverableInStore", "deliverableInStoreOnly")}</div>;
    $[128] = clickAndCollect;
    $[129] = getDeliveryInfo;
    $[130] = homeDelivery;
    $[131] = isFlyout;
    $[132] = isNotCh;
    $[133] = isPreOrderActive;
    $[134] = isReleased;
    $[135] = productIsUnavailable;
    $[136] = t41;
  } else {
    t41 = $[136];
  }
  let t42;
  if ($[137] !== t28 || $[138] !== t35 || $[139] !== t39 || $[140] !== t41) {
    t42 = <div className={t28}>{t35}{t39}{t40}{t41}</div>;
    $[137] = t28;
    $[138] = t35;
    $[139] = t39;
    $[140] = t41;
    $[141] = t42;
  } else {
    t42 = $[141];
  }
  let t43;
  if ($[142] !== productTileClass || $[143] !== t22 || $[144] !== t23 || $[145] !== t24 || $[146] !== t27 || $[147] !== t42) {
    t43 = <article className={productTileClass}>{t22}{t23}{t24}{t27}{t42}</article>;
    $[142] = productTileClass;
    $[143] = t22;
    $[144] = t23;
    $[145] = t24;
    $[146] = t27;
    $[147] = t42;
    $[148] = t43;
  } else {
    t43 = $[148];
  }
  return t43;
}
function _temp3(image_0, idx) {
  if (!image_0 || idx > 5) {
    return null;
  }
  if (idx === 5) {
    return <div key={image_0.url} className={classNames(styles["product-tile__variants-element"], styles["product-tile__variants-element--more"])} />;
  }
  return <ImageComponent className={classNames(styles["product-tile__variants-element"])} key={image_0.url} src={image_0.url} width={24} height={24} alt="" />;
}
function _temp2(badge) {
  return badge !== ProductBadge.EXCLUSIVE_ONLINE;
}
function _temp(asset) {
  return asset.type === "TU";
}
export default function ProductTileComponent(props) {
  const $ = _c(14);
  const productTileContext = useProductTileContext();
  if (!productTileContext) {
    throw new Error("ProductTileComponent requires ProductTileContext.Provider.");
  }
  let t0;
  if ($[0] !== productTileContext.countryCode || $[1] !== productTileContext.fullPath || $[2] !== productTileContext.isWishlistEnabled || $[3] !== productTileContext.locale || $[4] !== productTileContext.namespacedIntl.formatMessage || $[5] !== productTileContext.popularityEventsEnabled || $[6] !== productTileContext.preOrderEnabled || $[7] !== productTileContext.pricesIncludeTax || $[8] !== productTileContext.sendClickEvent || $[9] !== productTileContext.sessionsData) {
    t0 = {
      countryCode: productTileContext.countryCode,
      formatMessage: productTileContext.namespacedIntl.formatMessage,
      fullPath: productTileContext.fullPath,
      isWishlistEnabled: productTileContext.isWishlistEnabled,
      locale: productTileContext.locale,
      popularityEventsEnabled: productTileContext.popularityEventsEnabled,
      preOrderEnabled: productTileContext.preOrderEnabled,
      pricesIncludeTax: productTileContext.pricesIncludeTax,
      sendClickEvent: productTileContext.sendClickEvent,
      sessionsData: productTileContext.sessionsData
    };
    $[0] = productTileContext.countryCode;
    $[1] = productTileContext.fullPath;
    $[2] = productTileContext.isWishlistEnabled;
    $[3] = productTileContext.locale;
    $[4] = productTileContext.namespacedIntl.formatMessage;
    $[5] = productTileContext.popularityEventsEnabled;
    $[6] = productTileContext.preOrderEnabled;
    $[7] = productTileContext.pricesIncludeTax;
    $[8] = productTileContext.sendClickEvent;
    $[9] = productTileContext.sessionsData;
    $[10] = t0;
  } else {
    t0 = $[10];
  }
  let t1;
  if ($[11] !== props || $[12] !== t0) {
    t1 = <ProductTileContent {...props} runtimeValues={t0} />;
    $[11] = props;
    $[12] = t0;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
}